import BigNumber from 'bignumber.js';

export const USDT = {
  tron:     { address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', decimals: 6 },
  ethereum: { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
  bsc:      { address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
  polygon:  { address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6 },
};

export const CHAIN_LABEL = {
  tron:     'Tron (TRC20)',
  ethereum: 'Ethereum (ERC20)',
  bsc:      'BSC (BEP20)',
  polygon:  'Polygon (ERC20)',
};

export const EVM_CHAINS = ['ethereum', 'bsc', 'polygon'];

export const DEFAULT_RPC = {
  ethereum: 'https://ethereum-rpc.publicnode.com',
  bsc:      'https://bsc-dataseed.binance.org',
  polygon:  'https://polygon-rpc.com',
};

const TRONGRID = 'https://api.trongrid.io';

const ERC20_BALANCE_ABI = ['function balanceOf(address) view returns (uint256)'];

function abortAfter(ms) {
  const ctrl = new AbortController();
  setTimeout(() => ctrl.abort(), ms);
  return ctrl.signal;
}

/**
 * Fetch USDT balance for a single address on a given chain.
 * @param {string} address
 * @param {'tron'|'ethereum'|'bsc'|'polygon'} chain
 * @param {string} [rpcUrl]   - EVM RPC URL (uses DEFAULT_RPC if omitted)
 * @returns {Promise<{ balance: string, raw: BigNumber }>}
 */
export async function fetchUsdtBalance(address, chain, rpcUrl) {
  if (chain === 'tron') return _tronUsdt(address);
  return _evmUsdt(address, chain, rpcUrl || DEFAULT_RPC[chain]);
}

async function _tronUsdt(address) {
  try {
    const res = await fetch(`${TRONGRID}/v1/accounts/${address}`, {
      headers: { Accept: 'application/json' },
      signal: abortAfter(8000),
    });
    const data = await res.json();
    const acct = (data.data || [])[0] || {};
    const trc20 = acct.trc20 || [];
    const usdtEntry = trc20.find(e => Object.keys(e)[0] === USDT.tron.address);
    if (!usdtEntry) return { balance: '0', raw: new BigNumber(0) };
    const raw = new BigNumber(Object.values(usdtEntry)[0] || '0');
    const bal = raw.div(new BigNumber(10).pow(USDT.tron.decimals));
    return { balance: bal.toFixed(USDT.tron.decimals), raw };
  } catch (e) {
    console.warn(`[usdt] Tron balance failed for ${address}:`, e.message);
    return { balance: '—', raw: new BigNumber(0), error: e.message };
  }
}

async function _evmUsdt(address, chain, rpcUrl) {
  const { decimals, address: contractAddr } = USDT[chain];
  try {
    const { ethers } = await import('ethers');
    const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
    const contract = new ethers.Contract(contractAddr, ERC20_BALANCE_ABI, provider);
    const raw = await Promise.race([
      contract.balanceOf(address),
      new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 8000)),
    ]);
    const bn = new BigNumber(raw.toString());
    const bal = bn.div(new BigNumber(10).pow(decimals));
    return { balance: bal.toFixed(decimals), raw: bn };
  } catch (e) {
    console.warn(`[usdt] EVM balance failed for ${address} on ${chain}:`, e.message);
    return { balance: '—', raw: new BigNumber(0), error: e.message };
  }
}

/**
 * Fetch USDT balances for multiple address+chain pairs in parallel.
 * @param {Array<{ address: string, chain: string, rpcUrl?: string }>} entries
 * @returns {Promise<Map<string, { balance: string, raw: BigNumber }>>}
 *   key = "address:chain"
 */
export async function fetchAllUsdtBalances(entries) {
  const results = await Promise.allSettled(
    entries.map(({ address, chain, rpcUrl }) => fetchUsdtBalance(address, chain, rpcUrl))
  );
  const map = new Map();
  entries.forEach(({ address, chain }, i) => {
    const key = `${address.toLowerCase()}:${chain}`;
    map.set(key, results[i].status === 'fulfilled'
      ? results[i].value
      : { balance: '—', raw: new BigNumber(0), error: 'fetch failed' });
  });
  return map;
}

/** Format a USDT balance string with thousand separators */
export function fmtUsdt(balance) {
  const n = parseFloat(String(balance).replace(/,/g, ''));
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}
